import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react'
import { useLocation } from 'react-router-dom'
import { useBreadcrumbLeaf } from '@/hooks/useBreadcrumb'
import type { AssistantActionView, AssistantDecidedActions, AssistantPageContext } from '@/lib/assistant'
import { assistantStorageKey, readAssistantPrefs, writeAssistantPrefs } from '@/lib/assistantShell'
import { useAuth } from '@/providers/auth'
import { usePermission } from '@/providers/permissions'

export type AssistantThreadLoad = 'ready' | 'pending' | 'loading' | 'error'

export interface AssistantThread {
  key: string
  conversationId: string | null
  load: AssistantThreadLoad
}

export interface AssistantBridge {
  conversationId: () => string | null
  pageContext: () => AssistantPageContext
}

interface AssistantContextValue {
  enabled: boolean
  open: boolean
  setOpen: (open: boolean) => void
  toggle: () => void
  ask: (text: string) => void
  triggerRef: RefObject<HTMLButtonElement | null>
}

export interface AssistantSessionValue {
  open: boolean
  thread: AssistantThread
  scope: string
  askSeq: number
  draft: string
  draftSeq: number
  decidedActions: AssistantDecidedActions
  bridge: AssistantBridge
  setDraft: (draft: string) => void
  rememberAction: (view: AssistantActionView) => void
  takePendingAsk: () => string | null
  newConversation: () => void
  openConversation: (conversationId: string) => void
  conversationRemoved: (conversationId: string) => void
  retryLoad: () => void
  setThreadLoad: (key: string, load: AssistantThreadLoad) => void
  setThreadConversation: (key: string, conversationId: string) => void
  resetThread: (key: string) => void
  close: () => void
}

interface AssistantState {
  scope: string
  open: boolean
  thread: AssistantThread
  draft: string
  draftSeq: number
  askSeq: number
  actions: Record<string, AssistantActionView>
}

const AssistantContext = createContext<AssistantContextValue>({
  enabled: false,
  open: false,
  setOpen: () => undefined,
  toggle: () => undefined,
  ask: () => undefined,
  triggerRef: { current: null }
})

const AssistantSessionContext = createContext<AssistantSessionValue | null>(null)

export const useAssistant = () => useContext(AssistantContext)

export function useAssistantSession(): AssistantSessionValue {
  const session = useContext(AssistantSessionContext)
  if (!session) throw new Error('useAssistantSession must be used inside AssistantProvider')
  return session
}

let threadSeq = 0

const nextThreadKey = () => `thread-${Date.now().toString(36)}-${++threadSeq}`

const freshThread = (): AssistantThread => ({ key: nextThreadKey(), conversationId: null, load: 'ready' })

const storedThread = (conversationId: string): AssistantThread => ({ key: nextThreadKey(), conversationId, load: 'pending' })

const isPhoneViewport = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches

function initialState(scope: string, storageKey: string | null): AssistantState {
  const prefs = storageKey ? readAssistantPrefs(storageKey) : { open: false, conversationId: null }
  return {
    scope,
    open: prefs.open && !isPhoneViewport(),
    thread: prefs.conversationId ? storedThread(prefs.conversationId) : freshThread(),
    draft: '',
    draftSeq: 0,
    askSeq: 0,
    actions: {}
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && !!target.closest('input, textarea, select, [contenteditable="true"]')
}

const OTHER_DIALOG_SELECTOR = '[role="dialog"][data-state="open"]:not([data-assistant]), [role="alertdialog"][data-state="open"]'

export function AssistantProvider({ children }: { children: ReactNode }) {
  const { user, company } = useAuth()
  const { hasPermission } = usePermission()
  const location = useLocation()
  const pageTitle = useBreadcrumbLeaf()
  const enabled = !!user.id && !!company.id && hasPermission('ai', 'write')
  const scope = enabled ? `${user.id}:${company.id}` : ''
  const storageKey = enabled ? assistantStorageKey(user.id, company.id) : null
  const [state, setState] = useState<AssistantState>(() => initialState(scope, storageKey))
  if (state.scope !== scope) setState(initialState(scope, storageKey))
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const pendingAskRef = useRef<string | null>(null)
  const contextRef = useRef<AssistantPageContext>({})
  const conversationRef = useRef<string | null>(null)
  const { open, thread } = state

  useEffect(() => {
    contextRef.current = { path: `${location.pathname}${location.search}`, title: pageTitle ?? undefined, userName: user.name || undefined }
  }, [location.pathname, location.search, pageTitle, user.name])

  useEffect(() => {
    conversationRef.current = thread.conversationId
  }, [thread.conversationId])

  useEffect(() => {
    if (storageKey && state.scope === scope) writeAssistantPrefs(storageKey, { open, conversationId: thread.conversationId })
  }, [storageKey, scope, state.scope, open, thread.conversationId])

  const bridge = useMemo<AssistantBridge>(() => ({ conversationId: () => conversationRef.current, pageContext: () => contextRef.current }), [])

  const updateThread = useCallback(
    (key: string, update: (thread: AssistantThread) => AssistantThread) =>
      setState(current => {
        if (current.thread.key !== key) return current
        const next = update(current.thread)
        return next === current.thread ? current : { ...current, thread: next }
      }),
    []
  )

  const setThreadLoad = useCallback(
    (key: string, load: AssistantThreadLoad) => updateThread(key, current => (current.load === load ? current : { ...current, load })),
    [updateThread]
  )

  const setThreadConversation = useCallback(
    (key: string, conversationId: string) =>
      updateThread(key, current => (current.conversationId === conversationId ? current : { ...current, conversationId })),
    [updateThread]
  )

  const resetThread = useCallback(
    (key: string) =>
      updateThread(key, () => {
        conversationRef.current = null
        return freshThread()
      }),
    [updateThread]
  )

  const setOpen = useCallback((next: boolean) => setState(current => (current.open === next ? current : { ...current, open: next })), [])

  const toggle = useCallback(() => setState(current => ({ ...current, open: !current.open })), [])

  const close = useCallback(() => {
    setOpen(false)
    requestAnimationFrame(() => triggerRef.current?.focus())
  }, [setOpen])

  const ask = useCallback((text: string) => {
    const value = text.trim()
    if (!value) return
    pendingAskRef.current = value
    setState(current => ({ ...current, open: true, askSeq: current.askSeq + 1 }))
  }, [])

  const takePendingAsk = useCallback(() => {
    const value = pendingAskRef.current
    pendingAskRef.current = null
    return value
  }, [])

  const setDraft = useCallback((draft: string) => setState(current => (current.draft === draft ? current : { ...current, draft })), [])

  const newConversation = useCallback(() => {
    conversationRef.current = null
    setState(current => ({ ...current, thread: freshThread(), draft: '', draftSeq: current.draftSeq + 1 }))
  }, [])

  const rememberAction = useCallback(
    (view: AssistantActionView) =>
      setState(current =>
        current.actions[view.actionId] === view ? current : { ...current, actions: { ...current.actions, [view.actionId]: view } }
      ),
    []
  )

  const openConversation = useCallback((conversationId: string) => {
    setState(current => {
      if (current.thread.conversationId === conversationId && current.thread.load !== 'error') return current
      conversationRef.current = conversationId
      return { ...current, thread: storedThread(conversationId) }
    })
  }, [])

  const conversationRemoved = useCallback((conversationId: string) => {
    setState(current => {
      if (current.thread.conversationId !== conversationId) return current
      conversationRef.current = null
      return { ...current, thread: freshThread() }
    })
  }, [])

  const retryLoad = useCallback(
    () => setState(current => (current.thread.load === 'error' ? { ...current, thread: { ...current.thread, load: 'pending' } } : current)),
    []
  )

  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey || event.key.toLowerCase() !== 'j' || event.defaultPrevented) return
      const insideAssistant = event.target instanceof HTMLElement && !!event.target.closest('[data-assistant]')
      if (!insideAssistant && isTypingTarget(event.target)) return
      if (!insideAssistant && document.querySelector(OTHER_DIALOG_SELECTOR)) return
      event.preventDefault()
      toggle()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled, toggle])

  const contextValue = useMemo<AssistantContextValue>(
    () => ({ enabled, open: enabled && open, setOpen, toggle, ask, triggerRef }),
    [enabled, open, setOpen, toggle, ask]
  )

  const sessionValue = useMemo<AssistantSessionValue>(
    () => ({
      open: enabled && open,
      thread,
      scope,
      askSeq: state.askSeq,
      draft: state.draft,
      draftSeq: state.draftSeq,
      decidedActions: state.actions,
      bridge,
      setDraft,
      rememberAction,
      takePendingAsk,
      newConversation,
      openConversation,
      conversationRemoved,
      retryLoad,
      setThreadLoad,
      setThreadConversation,
      resetThread,
      close
    }),
    [
      enabled,
      open,
      thread,
      scope,
      state.askSeq,
      state.draft,
      state.draftSeq,
      state.actions,
      bridge,
      setDraft,
      rememberAction,
      takePendingAsk,
      newConversation,
      openConversation,
      conversationRemoved,
      retryLoad,
      setThreadLoad,
      setThreadConversation,
      resetThread,
      close
    ]
  )

  return (
    <AssistantContext.Provider value={contextValue}>
      <AssistantSessionContext.Provider value={enabled ? sessionValue : null}>{children}</AssistantSessionContext.Provider>
    </AssistantContext.Provider>
  )
}
